import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import {
  useGetUpcomingOrders,
  useCanSkipOrder,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type { SMPFutureOrder } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { formatSMPDate } from './utils/smpHelpers';
import { ShimmerImage } from './components';

const ORDERS_AMOUNT_WITH_RECOMMENDATION = 4;
const ORDERS_AMOUNT_WITHOUT_RECOMMENDATION = 1;

const FutureOrderRow = ({ order }: { order: SMPFutureOrder }) => {
  const firstLineItem = order.line_items?.[0];
  const productName = firstLineItem?.product_name || firstLineItem?.title;
  const productImage = firstLineItem?.image;
  const orderDate = order.date ? formatSMPDate(order.date) : 'Pending';

  return (
    <div className="flex items-center gap-5 py-5 border-b border-stroke-secondary-subtle">
      {productImage && (
        <ShimmerImage src={productImage} alt={productName} containerClassName="size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0" className="size-full object-cover" />
      )}

      <div className="flex flex-col gap-2">
        <span className="typography-body text-primary">{productName}</span>
        <span className="typography-caption1 text-tertiary">
          Order date: {orderDate}
        </span>
        {order.is_skipped && (
          <span className="typography-caption1 text-warning">
            Order Skipped
          </span>
        )}
      </div>
    </div>
  );
};

export const SMPFutureOrdersPage = () => {
  const { smpGoBack } = useSMPNavigate();
  const goBack = () => smpGoBack();
  const {
    subscriptionId,
    queryParams,
    hasRecommendation,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  const {
    data: upcomingOrders,
    isLoading: isLoadingOrders,
    isError: isErrorOrders,
  } = useGetUpcomingOrders(queryParams, {
    enabled: isSessionReady && !!queryParams.subscriptionId && !!queryParams.customerId,
  });

  const { data: canSkipData } = useCanSkipOrder(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const isLoading = isSMPLoading || isLoadingOrders;
  const isError = isSMPError || isErrorOrders;

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const ordersLimit = hasRecommendation
    ? ORDERS_AMOUNT_WITH_RECOMMENDATION
    : ORDERS_AMOUNT_WITHOUT_RECOMMENDATION;

  const canSkip = canSkipData?.can_skip ?? true;

  const orders: SMPFutureOrder[] = (upcomingOrders ?? [])
    .slice(0, ordersLimit)
    .map((order, index) => {
      if (index === 0 && !canSkip) {
        return { ...order, is_skipped: true };
      }
      return order;
    });

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-futureOrdersPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <h1 className="typography-h6 text-primary pt-5">Order Details</h1>
      </div>

      <div className="flex flex-col">
        {orders.length > 0 ? (
          orders.map((order) => (
            <FutureOrderRow key={order.id} order={order} />
          ))
        ) : (
          <div className="py-5">
            <span className="typography-body text-secondary">
              No upcoming orders.
            </span>
          </div>
        )}
      </div>
    </section>
  );
};
